import * as React from "react";
import { cn } from "../lib/utils";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "./pagination";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

export function DataTablePagination({
  table,
  pageSize,
  pageIndex,
  pageCount,
  startRow,
  endRow,
  totalRows,
  t,
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-6 text-sm text-muted-foreground">
        <label className="flex items-center gap-2 min-h-[44px]">
          <span className="whitespace-nowrap">
            {t("dataTable.rowsPerPage")}
          </span>
          <select
            className="h-11 min-h-[44px] rounded-lg border border-border bg-input px-3 text-sm text-foreground"
            value={pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            aria-label={t("dataTable.rowsPerPage")}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <p className="tabular-nums">
          {typeof t("dataTable.showing", {
            start: startRow,
            end: endRow,
            total: totalRows,
          }) === "string"
            ? t("dataTable.showing", {
                start: startRow,
                end: endRow,
                total: totalRows,
              })
            : `Showing ${startRow}-${endRow} of ${totalRows}`}
        </p>
      </div>

      <Pagination className="mx-0 w-full justify-end">
        <PaginationContent className="flex-wrap justify-end">
          <PaginationItem>
            <PaginationPrevious
              type="button"
              aria-label={t("common:previous", "Previous")}
              onClick={() => table.previousPage()}
              className={cn(
                "min-h-[44px]",
                !table.getCanPreviousPage() &&
                  "pointer-events-none opacity-40",
              )}
              aria-disabled={!table.getCanPreviousPage()}
            />
          </PaginationItem>

          {/* Compact page indicator on mobile (<sm) */}
          <PaginationItem className="sm:hidden">
            <span className="flex h-11 min-h-[44px] items-center px-3 text-sm text-muted-foreground tabular-nums">
              {pageIndex + 1} / {pageCount || 1}
            </span>
          </PaginationItem>

          {/* Full page numbers on tablet/desktop (>=sm) */}
          {pageCount > 0 && pageCount <= 10 ? (
            Array.from({ length: pageCount }, (_, i) => (
              <PaginationItem key={i} className="hidden sm:block">
                <PaginationLink
                  type="button"
                  isActive={pageIndex === i}
                  onClick={() => table.setPageIndex(i)}
                  size="icon"
                  className="min-h-[44px] min-w-[44px]"
                >
                  {i + 1}
                </PaginationLink>
              </PaginationItem>
            ))
          ) : pageCount > 10 ? (
            <PaginationItem className="hidden sm:block">
              <span className="flex h-11 min-h-[44px] items-center px-3 text-sm text-muted-foreground tabular-nums">
                {pageIndex + 1} / {pageCount}
              </span>
            </PaginationItem>
          ) : null}

          <PaginationItem>
            <PaginationNext
              type="button"
              aria-label={t("common:next", "Next")}
              onClick={() => table.nextPage()}
              className={cn(
                "min-h-[44px]",
                !table.getCanNextPage() &&
                  "pointer-events-none opacity-40",
              )}
              aria-disabled={!table.getCanNextPage()}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}

export { PAGE_SIZE_OPTIONS };
